/**
 * planEngine.js — attention-friendly "one next thing" engine.
 *
 * Answers: "What should this child do RIGHT NOW?"
 *
 * No new subsystem — this reads existing state (children, mastery,
 * progress, tier) and returns:
 *   • `next`  — the ONE next activity meta (id, label, emoji, to, minutes)
 *   • `today` — a 3-5 item ordered plan for today (dots strip)
 *   • `planSize` — adaptive daily size (floor 1, cap 5)
 *   • `markStarted(id)`, `markDone(id)` — persist progress
 *
 * Adaptive sizing: if the child regularly finishes fewer items than
 * planned, planSize quietly shrinks (floor 1). If they finish the
 * whole plan for a few days, it grows again (cap 5).
 * Never announced, never punished.
 *
 * Storage: `mwq_today_plan_v1` in localStorage, keyed by ISO date so
 * a new plan generates every calendar day.
 */

import { getActiveChild } from "@/lib/children";
import { loadProgress } from "@/lib/progress";
import { useTier } from "@/lib/ageGroup";
import { useEffect, useMemo, useState } from "react";

const PLAN_KEY = "mwq_today_plan_v1";
const HISTORY_KEY = "mwq_plan_history_v1"; // last 7 days of {date, planSize, doneCount}
const RESUME_KEY  = "mwq_resume_v1";       // { to, label, savedAt, meta? }

// Attention-honest minutes are our promise to the child. Kept low so
// "how much is left" is always answerable. Never inflate.
const CATALOG = {
  seedlings: [
    { id: "phonics",   label: "Read a sound",       emoji: "🔤", to: "/early-reading",          minutes: 4 },
    { id: "globe",     label: "Meet a country",     emoji: "🌍", to: "/first-quest",             minutes: 3 },
    { id: "art",       label: "Colour a landmark",  emoji: "🎨", to: "/coloring",                minutes: 5 },
    { id: "quiz",      label: "3 quick questions",  emoji: "🧠", to: "/quiz?mode=daily",         minutes: 3 },
    { id: "match",     label: "Match countries",    emoji: "🚩", to: "/games?tab=sprint",        minutes: 4 },
  ],
  explorers: [
    { id: "academy",   label: "Maths practice",     emoji: "🧮", to: "/academy",                 minutes: 5 },
    { id: "reading",   label: "Read a passage",     emoji: "📖", to: "/early-reading",           minutes: 5 },
    { id: "globe",     label: "Meet a country",     emoji: "🌍", to: "/first-quest",             minutes: 4 },
    { id: "quiz",      label: "5 quick questions",  emoji: "🧠", to: "/quiz?mode=daily",         minutes: 4 },
    { id: "sprint",    label: "Flag Sprint",        emoji: "🚩", to: "/games?tab=sprint",        minutes: 3 },
  ],
  pioneers: [
    { id: "academy",   label: "Skill practice",     emoji: "🎯", to: "/academy",                 minutes: 7 },
    { id: "reading",   label: "Read + reflect",     emoji: "📖", to: "/early-reading",           minutes: 6 },
    { id: "globe",     label: "Country deep-dive",  emoji: "🌍", to: "/first-quest",             minutes: 5 },
    { id: "quiz",      label: "Mastery check",      emoji: "🧠", to: "/quiz?mode=daily",         minutes: 5 },
    { id: "code",      label: "Code a step",        emoji: "💻", to: "/code-quest",              minutes: 6 },
  ],
};

const _today = () => new Date().toISOString().slice(0, 10);
const _read = (k) => {
  try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; }
};
const _write = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ }
};

/** Adaptive plan size — silently shrinks/grows based on last-7-day
 *  completion. Never surfaced as a metric the child sees. Floor 1, cap 5. */
function _adaptivePlanSize() {
  const hist = _read(HISTORY_KEY) || [];
  if (hist.length < 3) return 3; // sensible default until we have signal
  const recent = hist.slice(-5);
  const avg = recent.reduce((s, d) => s + (d.doneCount || 0), 0) / recent.length;
  // Round toward what they actually finish; never below 1, never above 5.
  return Math.max(1, Math.min(5, Math.round(avg || 1)));
}

/** Build the daily plan if today's isn't cached yet. */
function _ensureTodaysPlan(tier) {
  const cached = _read(PLAN_KEY);
  if (cached && cached.date === _today()) return cached;
  const key = tier || "explorers";
  const pool = CATALOG[key] || CATALOG.explorers;
  const size = _adaptivePlanSize();
  // Rotate the pool so consecutive days don't lead with the same activity.
  const dayIdx = Math.floor(Date.now() / 86400000);
  const rotated = pool.slice(dayIdx % pool.length).concat(pool.slice(0, dayIdx % pool.length));
  const plan = {
    date: _today(),
    planSize: size,
    items: rotated.slice(0, size),
    doneIds: [],
  };
  _write(PLAN_KEY, plan);
  return plan;
}

export function getTodayPlan(tier) {
  return _ensureTodaysPlan(tier);
}

/** Which activity is the ONE next thing? First un-done item wins. */
export function getNextActivity(tier) {
  const plan = _ensureTodaysPlan(tier);
  return plan.items.find((it) => !plan.doneIds.includes(it.id)) || null;
}

export function markPlanItemDone(id) {
  const plan = _read(PLAN_KEY);
  if (!plan) return;
  if (!plan.doneIds.includes(id)) plan.doneIds.push(id);
  _write(PLAN_KEY, plan);
  // Roll into history at end of day (best-effort — recorded now so if
  // they never return, the completion still counts).
  const hist = _read(HISTORY_KEY) || [];
  const todayEntry = hist.find((h) => h.date === plan.date);
  if (todayEntry) todayEntry.doneCount = plan.doneIds.length;
  else hist.push({ date: plan.date, planSize: plan.planSize, doneCount: plan.doneIds.length });
  // Cap history at 14 days.
  _write(HISTORY_KEY, hist.slice(-14));
  try { window.dispatchEvent(new Event("mwq-plan-changed")); } catch { /* ignore */ }
}

/** Persist a resume-point so "Continue" on Home/MyLearning works. */
export function setResumePoint({ to, label, meta = null }) {
  if (!to) return;
  _write(RESUME_KEY, { to, label: label || "Continue where you left off", savedAt: Date.now(), meta });
  try { window.dispatchEvent(new Event("mwq-resume-changed")); } catch { /* ignore */ }
}

export function getResumePoint() {
  const r = _read(RESUME_KEY);
  // Auto-expire resume points after 48h so they never feel stale.
  if (!r || !r.savedAt || Date.now() - r.savedAt > 48 * 3600 * 1000) return null;
  return r;
}

export function clearResumePoint() {
  try { localStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
  try { window.dispatchEvent(new Event("mwq-resume-changed")); } catch { /* ignore */ }
}

/** React hook — the primary interface for pages/components. */
export function usePlanEngine() {
  const tier = useTier() || "explorers";
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const refresh = () => setTick((n) => n + 1);
    window.addEventListener("mwq-plan-changed", refresh);
    window.addEventListener("we-progress-changed", refresh);
    window.addEventListener("we-active-child-changed", refresh);
    return () => {
      window.removeEventListener("mwq-plan-changed", refresh);
      window.removeEventListener("we-progress-changed", refresh);
      window.removeEventListener("we-active-child-changed", refresh);
    };
  }, []);
  const plan = useMemo(() => _ensureTodaysPlan(tier), [tier, tick]);
  const next = useMemo(() => plan.items.find((it) => !plan.doneIds.includes(it.id)) || null, [plan]);
  // Consumers use this to know if the day is complete.
  const allDone = plan.items.length > 0 && plan.doneIds.length >= plan.items.length;
  // Very lightweight learner ping so the strip greeting can adapt.
  const kid = getActiveChild();
  return {
    tier,
    plan,
    next,
    allDone,
    childName: kid?.name || "",
    stars: loadProgress()?.stars || 0,
    markDone: markPlanItemDone,
    setResume: setResumePoint,
  };
}
